import {runInAction} from 'mobx'
import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef} from 'react'
import {Debouncer, HTMLUtil} from 'yougile-util'
import {Hub, newsCompanyId} from 'yougile-model'
import {Sync} from '@client/sync'
import {ReactPortal} from '@components/common/react-portal/react-portal'
import {useRightPanelContentStore} from '../right-panel-content/right-panel-content-store'
import {CustomCKEditor} from './ck-editor'
import {TwFullscreenContainer, TwMainContainer} from './containers'
import {EditorContainer} from './editor-container'
import {useOnObjectsClick} from './hooks/use-on-objects-click'
import {useSyncDescription} from './hooks/use-sync-description'
import {useTaskDescription} from './task-description-store'
import {TaskTitleInput} from './task-title-input'
import {TopPanel} from './top-panel/top-panel'

type Props = {
  hub: Hub
  headerHeight: number
  newBg?: boolean
}

const runLatestDebouncer = new Debouncer(100)

export const TaskDescriptionLayout = observer((props: Props) => {
  const {hub, headerHeight, newBg} = props
  const [state, store] = useTaskDescription()
  const editorContainerRef = useRef<HTMLDivElement>()
  const {edit, fullscreen} = state

  const isNews = hub && hub.companyId === newsCompanyId
  const canEditNews = Sync.isNewsEditor
  const showTopPanel = (isNews && canEditNews) || !isNews
  const [, panelStore] = useRightPanelContentStore()

  useSyncDescription({hub})

  useEffect(() => {
    let el: null | Element
    let handler: () => void

    setTimeout(() => {
      el = document.querySelector('.ck-editor .ck-content')
      if (!el) {
        return
      }

      handler = () => {
        runLatestDebouncer.runLatest(() => {
          const scroll = el?.scrollTop !== undefined ? el?.scrollTop : -1
          panelStore.setDescriptionScroll(scroll)
        })
      }

      el.addEventListener('scroll', handler)
    }, 200)

    return () => {
      if (handler && el) {
        el.removeEventListener('scroll', handler)
      }
    }
  }, [hub?.id])

  const processImageClick = useOnObjectsClick(edit)

  useEffect(() => {
    store.setEdit(false)
    setTimeout(() => {
      runInAction(() => {
        const html = HTMLUtil.sanitizeHtml(
          hub?.description || '',
          isNews
            ? {
                allowedTags: ['iframe'],
                allowedAttributes: {
                  iframe: [
                    'width',
                    'height',
                    'src',
                    'title',
                    'frameborder',
                    'allow',
                    'referrerpolicy',
                    'allowfullscreen'
                  ],
                  figure: ['class']
                }
              }
            : {}
        )
        store.taskHub = hub
        store.updateContent(html)
      })
    }, 10)
    return () => {
      // force save with previous taskId
      void store.saveContent()
    }
  }, [hub?.id])

  const offset = isNews || fullscreen ? 0 : headerHeight + 65 + (edit ? 40 : 0)

  return (
    <ReactPortal elementId="description-fullscreen" enabled={fullscreen}>
      <TwMainContainer
        id={'description-editor'}
        $fullscreen={fullscreen}
        onClick={processImageClick}
      >
        {showTopPanel && <TopPanel newBg={newBg} />}
        <TwFullscreenContainer
          $fullscreen={fullscreen}
          style={fullscreen ? {maxHeight: 'calc(100vh - 64px)'} : {}}
        >
          {fullscreen && <TaskTitleInput />}
          <EditorContainer editorContainerRef={editorContainerRef}>
            <CustomCKEditor
              readonly={!edit}
              headerHeight={offset}
              value={
                state.contentChanged /* to save edited data when toggle to fullscreen mode */
              }
              onChange={store.onChange}
              kindOf={isNews ? 'news-description' : 'task-description'}
            />
          </EditorContainer>
        </TwFullscreenContainer>
      </TwMainContainer>
    </ReactPortal>
  )
})
